import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { ArrowDown, ArrowUp, GripVertical, Play, Plus, Trash2, X } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { cn } from 'lib/utils';
import { formatClock } from '../../constants';
import type { SectionStimulus } from '../../types';

/** Drag ids: a clip by its place in the list, and the line below that clip. */
export const clipId = (index: number) => `clip:${index}`;
export const dividerId = (index: number) => `divider:${index}`;

interface ClipRowProps {
  index: number;
  clip: SectionStimulus['tracks'][number];
  count: number;
  disabled?: boolean;
  /** Where a dragged clip would land next to this one. */
  indicator: 'before' | 'after' | null;
  previewUrl: string | null;
  onRename: (label: string) => void;
  onPreview: () => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}

/** One audio clip: drag handle, name, length, and the up/down/remove buttons. */
export function AudioClipRow({ index, clip, count, disabled, indicator, previewUrl, onRename, onPreview, onMove, onRemove }: ClipRowProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef: setDragRef, setActivatorNodeRef, isDragging } = useDraggable({ id: clipId(index), disabled });
  const { setNodeRef: setDropRef } = useDroppable({ id: clipId(index) });
  const setRefs = useCallback(
    (node: HTMLLIElement | null) => {
      setDragRef(node);
      setDropRef(node);
    },
    [setDragRef, setDropRef],
  );

  return (
    <li
      ref={setRefs}
      className={cn('relative flex flex-wrap items-center gap-2 rounded-md border border-border bg-card px-3 py-2', isDragging && 'opacity-40')}
    >
      {indicator && (
        <span
          data-drop-indicator={indicator}
          aria-hidden
          className={cn('pointer-events-none absolute inset-x-0 h-0.5 rounded bg-foreground', indicator === 'before' ? '-top-1.5' : '-bottom-1.5')}
        />
      )}
      <button
        type="button"
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        disabled={disabled}
        aria-label={t('exam.audio.drag')}
        title={t('exam.audio.drag')}
        className="flex h-8 w-6 shrink-0 cursor-grab touch-none items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
      >
        <GripVertical className="h-4 w-4" aria-hidden />
      </button>
      <span className="text-xs font-semibold text-muted-foreground">{index + 1}</span>
      <Input
        defaultValue={clip.label}
        disabled={disabled}
        aria-label={t('exam.audio.part_label', { n: index + 1 })}
        onBlur={(e) => {
          if (e.target.value !== clip.label) onRename(e.target.value);
        }}
        className="h-8 min-w-0 flex-1"
      />
      {clip.durationSec ? <span className="font-mono text-xs text-muted-foreground">{formatClock(clip.durationSec)}</span> : null}
      <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={onPreview} aria-label={t('exam.audio.preview')}>
        <Play className="h-4 w-4" aria-hidden />
      </Button>
      <Button type="button" size="icon" variant="ghost" className="h-8 w-8" disabled={disabled || index === 0} onClick={() => onMove(-1)} aria-label={t('exam.move_up')}>
        <ArrowUp className="h-4 w-4" aria-hidden />
      </Button>
      <Button type="button" size="icon" variant="ghost" className="h-8 w-8" disabled={disabled || index === count - 1} onClick={() => onMove(1)} aria-label={t('exam.move_down')}>
        <ArrowDown className="h-4 w-4" aria-hidden />
      </Button>
      <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-destructive" disabled={disabled} onClick={onRemove} aria-label={t('exam.audio.remove')}>
        <Trash2 className="h-4 w-4" aria-hidden />
      </Button>
      {previewUrl && (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio src={previewUrl} controls autoPlay className="mt-1 w-full" />
      )}
    </li>
  );
}

interface DividerRowProps {
  index: number;
  active: boolean;
  partNumber: number;
  disabled?: boolean;
  /** A clip is being dragged over this line. */
  over: boolean;
  onToggle: () => void;
}

/** The line between two clips: add a divider here, or (when set) mark where the next part starts and offer to remove it. Also a drop target. */
export function AudioDividerRow({ index, active, partNumber, disabled, over, onToggle }: DividerRowProps) {
  const { t } = useTranslation();
  const { setNodeRef } = useDroppable({ id: dividerId(index) });
  const label = active ? t('exam.audio.remove_divider') : t('exam.audio.add_divider');
  const Icon = active ? X : Plus;
  const line = cn('flex-1', over ? 'h-0.5 bg-foreground' : active ? 'h-px bg-foreground/40' : 'border-t border-dashed border-border');
  return (
    <li
      ref={setNodeRef}
      role={active ? 'separator' : undefined}
      data-drop-indicator={over ? 'divider' : undefined}
      className="flex items-center gap-2 text-xs font-semibold text-foreground"
    >
      <span className={line} aria-hidden />
      {active && t('exam.listening.part', { n: partNumber })}
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className={cn('h-6 w-6', !active && 'text-muted-foreground')}
        disabled={disabled}
        onClick={onToggle}
        title={label}
        aria-label={label}
      >
        <Icon className="h-3.5 w-3.5" aria-hidden />
      </Button>
      <span className={line} aria-hidden />
    </li>
  );
}
